
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider, Outlet, RouteObject } from "react-router-dom";
import { CartProvider, useCart } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { PlatformProvider } from "@/contexts/PlatformContext";
import { useEffect, useState, useMemo, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { authRoutes } from "@/routes/authRoutes";
import { profileRoutes } from "@/routes/profileRoutes";
import { sellerRoutes } from "@/routes/sellerRoutes";
import { mainRoutes } from "@/routes/mainRoutes";
import { adminRoutes } from "@/routes/adminRoutes";
import { Session } from "@supabase/supabase-js";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingTransition } from "@/components/LoadingTransition";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { createLogger } from "@/utils/logger";
import { AuthModal } from "@/components/auth/AuthModal";

const logger = createLogger('App');

// Session storage key for tracking loading screen display
const LOADING_SHOWN_KEY = 'homeplate_loading_shown';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
    },
  },
});

// Smaller loading component for route transitions
const RouteLoading = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
  </div>
);

// Wrapper component that manages auth and cart integration
const AuthWithCart = ({ children }: { children: React.ReactNode }) => {
  const { addItem } = useCart();
  
  return (
    <AuthProvider addItemToCart={addItem}>
      {children}
    </AuthProvider>
  );
};

// Wrap the main content in a component to prevent re-mounting of Suspense boundaries
const MainContent = ({ session }: { session: Session | null }) => {
  const routes = useMemo<RouteObject[]>(() => [
    {
      path: "/",
      element: (
        <PlatformProvider>
          <CartProvider>
            <AuthWithCart>
              <TooltipProvider>
                <MainLayout>
                  <Toaster />
                  <AuthModal />
                  <Suspense fallback={<RouteLoading />}>
                    <Outlet />
                  </Suspense>
                </MainLayout>
              </TooltipProvider>
            </AuthWithCart>
          </CartProvider>
        </PlatformProvider>
      ),
      children: [
        ...mainRoutes({ session }),
        ...profileRoutes({ session }),
        ...sellerRoutes({ session }),
        ...adminRoutes({ session })
      ]
    },
    ...authRoutes({ session })
  ], [session]);

  const router = useMemo(() => createBrowserRouter(routes), [routes]);

  return (
    <RouterProvider router={router} />
  );
};

const App = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if loading screen has been shown in this session
  const shouldShowLoading = useMemo(() => {
    try {
      // Check session storage specifically for loading screen
      return !sessionStorage.getItem(LOADING_SHOWN_KEY);
    } catch (error) {
      logger.error('Error checking session storage:', error);
      return true; // Default to showing on error
    }
  }, []);

  useEffect(() => {
    logger.info('Initializing auth state');
    
    // Initialize auth and mark loading as shown in parallel
    Promise.all([
      // Get initial session
      supabase.auth.getSession().then(({ data: { session } }) => {
        logger.info('Initial session loaded');
        setSession(session);
      }),
      
      // Ensure minimum loading time if we need to show loading
      new Promise<void>(resolve => {
        if (shouldShowLoading) {
          // Keep loading for a bit
          logger.debug("Showing loading screen (first visit this session)");
          // Loading will be handled by LoadingTransition component
        } else {
          logger.debug("Skipping loading screen (already shown this session)");
          // No loading screen needed, resolve immediately
          resolve();
        }
      })
    ]).then(() => {
      if (!shouldShowLoading) {
        // If we're not showing loading, finish immediately
        setIsLoading(false);
      }
      // Otherwise, loading will be handled by LoadingTransition component
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      logger.info('Auth state changed', _event);
      setSession(session);
    });

    // Safari fix: Reset any background colors that might persist
    document.body.style.backgroundColor = "";
    document.documentElement.style.backgroundColor = "";

    return () => {
      logger.debug('Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, [shouldShowLoading]);

  // Handle loading transition completion
  const handleLoadingComplete = () => {
    logger.debug("Loading transition complete, marking as shown");
    
    try {
      // Mark loading as shown in this session
      sessionStorage.setItem(LOADING_SHOWN_KEY, 'true');
      
      // Safari fix: Add a small delay to ensure background is cleared
      setTimeout(() => {
        document.body.style.backgroundColor = "";
        document.documentElement.style.backgroundColor = "";
        document.body.style.overflow = "auto"; // Ensure scrolling is enabled
        setIsLoading(false);
      }, 50);
    } catch (error) {
      logger.error('Error writing to session storage:', error);
      setIsLoading(false);
    }
  };

  // Separate the loading transition from the main content render
  if (isLoading && shouldShowLoading) {
    return (
      <QueryClientProvider client={queryClient}>
        <LoadingTransition onComplete={handleLoadingComplete} />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <MainContent session={session} />
      </ErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
